/**
 * Cliente HTTP tipado de la Fitness API.
 *
 * No depende de React ni de ninguna API exclusiva de Node: solo usa `fetch`,
 * `Promise` y `Date`, así que funciona igual en el navegador y en React Native
 * (Hermes).
 */
import createClient, {} from 'openapi-fetch';
import { ApiClientError, NetworkError } from './errors.js';
import { toApiClientError, unwrap, unwrapVoid } from './http.js';
import { createExercisesApi } from './namespaces/exercises.js';
import { createRemindersApi } from './namespaces/reminders.js';
import { createHabitsApi } from './namespaces/habits.js';
import { createFeedApi } from './namespaces/feed.js';
import { createFoodsApi } from './namespaces/foods.js';
import { createMealsApi } from './namespaces/meals.js';
import { createMediaApi } from './namespaces/media.js';
import { createNutritionApi } from './namespaces/nutrition.js';
import { createPostsApi } from './namespaces/posts.js';
import { createProgressApi } from './namespaces/progress.js';
import { createRecipesApi } from './namespaces/recipes.js';
import { createOAuthApi } from './namespaces/oauth.js';
import { createReportsApi } from './namespaces/reports.js';
import { createRoutinesApi } from './namespaces/routines.js';
import { createUsersApi } from './namespaces/users.js';
import { createWorkoutsApi } from './namespaces/workouts.js';
import { MemoryTokenStorage, sessionFromTokenPair, } from './session.js';
/** Rutas que no llevan bearer ni disparan refrescos. */
const PUBLIC_PATHS = [
    '/healthz',
    '/v1/auth/register',
    '/v1/auth/login',
    '/v1/auth/refresh',
    '/v1/auth/logout',
    '/v1/auth/providers',
    '/v1/auth/social/complete',
    '/v1/auth/social/signup',
];
const DEFAULT_REFRESH_SKEW_MS = 30_000;
/**
 * Crea un cliente de la Fitness API.
 *
 * @example
 * const api = createApiClient({
 *   baseUrl: 'http://localhost:8080',
 *   storage: createWebStorageTokenStorage(window.localStorage),
 * });
 * await api.auth.login({ email, password });
 * const me = await api.me.get();
 */
export function createApiClient(options) {
    const storage = options.storage ?? new MemoryTokenStorage();
    const refreshSkewMs = options.refreshSkewMs ?? DEFAULT_REFRESH_SKEW_MS;
    const userFetch = options.fetch;
    const inCookie = options.authTransport === 'cookie';
    /**
     * Opciones de las llamadas que emiten, leen o borran el token de refresco.
     * Con la cookie, la cabecera pide ese transporte y `credentials` deja que el
     * navegador envíe y guarde la cookie aunque la API viva en otro puerto.
     */
    const transport = inCookie
        ? {
            params: { header: { 'X-Auth-Transport': 'cookie' } },
            credentials: 'include',
        }
        : {};
    const baseFetch = userFetch
        ? (request) => userFetch(request)
        : (request) => globalThis.fetch(request);
    /** `fetch` que traduce los fallos de transporte a {@link NetworkError}. */
    async function safeFetch(request) {
        try {
            return await baseFetch(request);
        }
        catch (cause) {
            if (cause instanceof NetworkError || cause instanceof ApiClientError)
                throw cause;
            throw new NetworkError(`No se ha podido contactar con el servidor (${request.method} ${request.url}).`, { cause });
        }
    }
    /**
     * `fetch` con reintento: ante un 401 en una ruta autenticada renueva la
     * sesión (una sola vez, compartida entre peticiones concurrentes) y repite
     * la petición original con el token nuevo.
     */
    const authFetch = async (request) => {
        if (isPublicUrl(request.url))
            return safeFetch(request);
        // Hay que clonar ANTES de consumir el cuerpo para poder reintentar.
        const retryable = typeof request.clone === 'function' ? request.clone() : null;
        const usedToken = readBearer(request);
        const response = await safeFetch(request);
        if (response.status !== 401 || retryable === null)
            return response;
        const session = await recoverFrom401(usedToken);
        if (session === null)
            return response;
        retryable.headers.set('Authorization', `Bearer ${session.accessToken}`);
        return safeFetch(retryable);
    };
    const client = createClient({
        baseUrl: options.baseUrl,
        fetch: authFetch,
        ...(options.headers ? { headers: options.headers } : {}),
    });
    /**
     * Envía un `FormData` sin pasar por `openapi-fetch`, para la subida de
     * imágenes: ese cliente serializa el cuerpo a JSON y el contrato declara el
     * archivo como `string`, así que el multipart se compone aquí.
     *
     * Reproduce a mano lo que hace el middleware —bearer y refresco proactivo— y
     * reutiliza `authFetch`, de modo que un 401 renueva la sesión y reintenta
     * igual que en cualquier otra petición.
     */
    async function postMultipart(path, body) {
        const headers = new Headers(options.headers ?? {});
        // El boundary lo genera `fetch` al construir la petición a partir del
        // `FormData`. Fijar el `Content-Type` a mano lo perdería y el servidor no
        // sabría trocear el cuerpo, así que se quita si venía en las cabeceras
        // globales.
        headers.delete('Content-Type');
        const session = await ensureFreshSession();
        if (session)
            headers.set('Authorization', `Bearer ${session.accessToken}`);
        return authFetch(new Request(joinUrl(options.baseUrl, path), { method: 'POST', body, headers }));
    }
    /** Inyecta el bearer y refresca de forma proactiva antes de cada petición. */
    const authMiddleware = {
        async onRequest({ request, schemaPath }) {
            if (isPublicSchemaPath(schemaPath))
                return undefined;
            const session = await ensureFreshSession();
            if (session)
                request.headers.set('Authorization', `Bearer ${session.accessToken}`);
            return request;
        },
    };
    client.use(authMiddleware);
    // --- Sesión -------------------------------------------------------------
    function emitSessionChange(session) {
        try {
            options.onSessionChange?.(session);
        }
        catch {
            // Un listener roto no debe tumbar la petición en curso.
        }
    }
    function emitSignedOut() {
        try {
            options.onSignedOut?.();
        }
        catch {
            // Ídem.
        }
    }
    async function persist(authResponse) {
        const session = sessionFromTokenPair(authResponse.tokens);
        await storage.set(session);
        emitSessionChange(session);
        return session;
    }
    async function forgetSession(involuntary) {
        await storage.clear();
        emitSessionChange(null);
        if (involuntary)
            emitSignedOut();
    }
    let refreshInFlight = null;
    /** Un único refresco en vuelo: los concurrentes comparten la misma promesa. */
    function refreshSingleFlight() {
        if (refreshInFlight === null) {
            const pending = options.serializeRefresh
                ? options.serializeRefresh(performRefresh)
                : performRefresh();
            refreshInFlight = pending;
            const release = () => {
                if (refreshInFlight === pending)
                    refreshInFlight = null;
            };
            pending.then(release, release);
        }
        return refreshInFlight;
    }
    async function performRefresh() {
        const current = await storage.get();
        const refreshToken = current?.refreshToken;
        // Con la cookie no hace falta tener nada guardado: el navegador la envía
        // sola. Un `refreshToken` guardado (la sesión antigua de la web) se manda
        // en el cuerpo una única vez y el servidor lo cambia por la cookie.
        if (!refreshToken && !inCookie) {
            await forgetSession(true);
            throw new ApiClientError(401, 'unauthorized', 'No hay ninguna sesión que renovar.');
        }
        const result = await client.POST('/v1/auth/refresh', {
            body: refreshToken ? { refreshToken } : {},
            ...transport,
        });
        if (!result.response.ok || result.data === undefined) {
            const failure = toApiClientError(result.response, result.error);
            if (result.response.status === 401) {
                // Refresco rechazado (caducado, rotado o reutilizado): la sesión ya no
                // es recuperable.
                await forgetSession(true);
                throw new ApiClientError(401, 'unauthorized', failure.message, failure.details);
            }
            throw failure;
        }
        const authResponse = result.data;
        const session = await persist(authResponse);
        return { auth: authResponse, session };
    }
    /** Sesión vigente, refrescada de antemano si está a punto de caducar. */
    async function ensureFreshSession() {
        const session = await storage.get();
        if (session === null) {
            // Con la cookie, quedarse sin token de acceso (recarga de la página, o
            // una app que arrancó sin red) no es perder la sesión: se intenta
            // recuperarla antes de enviar la petición sin bearer.
            // Sin red la petición sale igualmente sin bearer: el service worker de
            // la web puede contestarla desde su caché, y si llega al servidor el 401
            // vuelve a intentar la renovación.
            if (!inCookie)
                return null;
            try {
                const { session: recovered } = await refreshSingleFlight();
                return recovered;
            }
            catch (error) {
                if (error instanceof NetworkError)
                    return null;
                throw error;
            }
        }
        if (session.expiresAt - Date.now() > refreshSkewMs)
            return session;
        const { session: renewed } = await refreshSingleFlight();
        return renewed;
    }
    /**
     * Decide cómo recuperarse de un 401. Si otra petición ya renovó la sesión
     * mientras ésta viajaba, basta con reintentar con el token nuevo.
     */
    async function recoverFrom401(usedToken) {
        const current = await storage.get();
        if (current === null && !inCookie)
            return null;
        if (current !== null && usedToken !== null && current.accessToken !== usedToken)
            return current;
        const { session } = await refreshSingleFlight();
        return session;
    }
    // --- API pública --------------------------------------------------------
    const auth = {
        async register(body) {
            const data = unwrap(await client.POST('/v1/auth/register', { body, ...transport }));
            await persist(data);
            return data;
        },
        async login(body) {
            const data = unwrap(await client.POST('/v1/auth/login', { body, ...transport }));
            await persist(data);
            return data;
        },
        async logout() {
            const current = await storage.get();
            try {
                if (current?.refreshToken) {
                    await client.POST('/v1/auth/logout', {
                        body: { refreshToken: current.refreshToken },
                        ...transport,
                    });
                }
                else if (inCookie) {
                    // El token está en la cookie: el servidor lo revoca y la borra.
                    await client.POST('/v1/auth/logout', { body: {}, ...transport });
                }
            }
            catch {
                // Cierre de sesión best-effort: la sesión local se borra igualmente.
            }
            finally {
                await forgetSession(false);
            }
        },
        async refresh() {
            const { auth: authResponse } = await refreshSingleFlight();
            return authResponse;
        },
        async providers() {
            return unwrap(await client.GET('/v1/auth/providers')).providers;
        },
        socialStartUrl(provider, startOptions = {}) {
            // A mano y no con `URLSearchParams`, que React Native no implementa
            // entero.
            const pairs = [
                ['redirect', startOptions.redirect],
                ['app_redirect', startOptions.appRedirect],
                ['code_challenge', startOptions.codeChallenge],
            ];
            const query = pairs
                .filter((pair) => typeof pair[1] === 'string' && pair[1] !== '')
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join('&');
            const suffix = query === '' ? '' : `?${query}`;
            return joinUrl(options.baseUrl, `/v1/auth/${encodeURIComponent(provider)}/start${suffix}`);
        },
        async completeSocial(body = {}) {
            const data = unwrap(await client.POST('/v1/auth/social/complete', { body, ...transport }));
            if (data.status === 'signed_in' && data.auth)
                await persist(data.auth);
            return data;
        },
        async socialSignup(body) {
            const data = unwrap(await client.POST('/v1/auth/social/signup', { body, ...transport }));
            await persist(data);
            return data;
        },
    };
    const me = {
        async get() {
            return unwrap(await client.GET('/v1/me'));
        },
        async setActiveRoutine(routineId) {
            return unwrap(await client.PUT('/v1/me/active-routine', { body: { routineId } }));
        },
        async update(body) {
            return unwrap(await client.PATCH('/v1/me', { body }));
        },
        async getProfile() {
            return unwrap(await client.GET('/v1/me/profile'));
        },
        async putProfile(body) {
            return unwrap(await client.PUT('/v1/me/profile', { body }));
        },
        async listTokens() {
            return unwrap(await client.GET('/v1/me/tokens'));
        },
        async createToken(body) {
            return unwrap(await client.POST('/v1/me/tokens', { body }));
        },
        async revokeToken(id) {
            unwrapVoid(await client.DELETE('/v1/me/tokens/{id}', { params: { path: { id } } }));
        },
        async listBodyMetrics(query) {
            return unwrap(await client.GET('/v1/me/body-metrics', { params: { query: query ?? {} } }));
        },
        async createBodyMetric(body) {
            return unwrap(await client.POST('/v1/me/body-metrics', { body }));
        },
        async updateBodyMetric(id, body) {
            return unwrap(await client.PUT('/v1/me/body-metrics/{id}', {
                params: { path: { id } },
                body,
            }));
        },
        async removeBodyMetric(id) {
            unwrapVoid(await client.DELETE('/v1/me/body-metrics/{id}', {
                params: { path: { id } },
            }));
        },
        async changePassword(body) {
            const data = unwrap(await client.POST('/v1/me/password', { body, ...transport }));
            // Las demás sesiones, la de este dispositivo incluida, ya no valen:
            // la nueva es la que devuelve el servidor.
            await persist(data);
            return data;
        },
        async deleteAccount(body) {
            unwrapVoid(await client.DELETE('/v1/me', { body, ...transport }));
            await forgetSession(false);
        },
        async loginMethods() {
            return unwrap(await client.GET('/v1/me/login-methods'));
        },
        async linkProvider(provider, body) {
            return unwrap(await client.POST('/v1/me/login-methods/{provider}', {
                params: { path: { provider } },
                body,
            }));
        },
        async confirmLink(provider, body = {}) {
            unwrapVoid(await client.POST('/v1/me/login-methods/{provider}/confirm', {
                params: {
                    path: { provider },
                    ...(inCookie ? { header: { 'X-Auth-Transport': 'cookie' } } : {}),
                },
                body,
                ...(inCookie ? { credentials: 'include' } : {}),
            }));
        },
        async unlinkProvider(provider, body) {
            unwrapVoid(await client.DELETE('/v1/me/login-methods/{provider}', {
                params: { path: { provider } },
                body,
            }));
        },
        async reauthenticate(provider, body) {
            return unwrap(await client.POST('/v1/me/login-methods/{provider}/reauth', {
                params: { path: { provider } },
                body,
            }));
        },
    };
    return {
        raw: client,
        reminders: createRemindersApi(client),
        habits: createHabitsApi(client),
        auth,
        me,
        exercises: createExercisesApi(client),
        routines: createRoutinesApi(client),
        workouts: createWorkoutsApi(client),
        progress: createProgressApi(client),
        foods: createFoodsApi(client),
        recipes: createRecipesApi(client),
        meals: createMealsApi(client),
        nutrition: createNutritionApi(client),
        users: createUsersApi(client),
        feed: createFeedApi(client),
        posts: createPostsApi(client),
        reports: createReportsApi(client),
        oauth: createOAuthApi(client),
        media: createMediaApi(client, { postMultipart }),
        async health() {
            const result = await client.GET('/healthz');
            // Un 503 es una respuesta válida de la sonda, no un fallo: el cuerpo trae
            // `{ status: 'degraded' }`. `openapi-fetch` lo deja en `error` porque la
            // respuesta no es `ok`.
            if (result.response.status === 503) {
                const body = result.error ?? result.data;
                return isHealthStatus(body) ? body : { status: 'degraded' };
            }
            return unwrap(result);
        },
        getSession() {
            return storage.get();
        },
        async isAuthenticated() {
            return (await storage.get()) !== null;
        },
    };
}
/** Cuerpo de `/healthz`, venga por `data` (200) o por `error` (503). */
function isHealthStatus(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const status = value.status;
    return status === 'ok' || status === 'degraded';
}
function isPublicSchemaPath(schemaPath) {
    return PUBLIC_PATHS.includes(schemaPath);
}
function isPublicUrl(url) {
    const withoutQuery = url.split('?')[0] ?? url;
    return PUBLIC_PATHS.some((path) => withoutQuery.endsWith(path));
}
/** Pega la ruta a la raíz de la API sin duplicar la barra del medio. */
function joinUrl(baseUrl, path) {
    return `${baseUrl.replace(/\/+$/, '')}${path}`;
}
function readBearer(request) {
    const header = request.headers.get('Authorization');
    if (header === null)
        return null;
    return header.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
}
//# sourceMappingURL=client.js.map